import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { VerificationBannerContext } from '../../components/layout/dashboard-layout';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import PaymentInvoiceAccordionContent from '../../components/tableComponent/PaymentInvoiceAccordionContent';
import { PaymentHistoryItemNew, PaymentInvoiceDetail } from '../../services/payment-services';
import { RootState } from '../../store';
import { formatCurrency, formatToUSDate } from '../../utils/helper';
import { getEqualWidthTableSx, mapPaymentHistoryRow, sortByDateDesc } from '../../utils/paymentHistoryHelpers';
import { useTranslation } from '../../utils/translations';

import { PaymentHistoryCard } from './components/PaymentHistoryCard';

interface PaymentHistoryProps {
    currentPage?: number;
    rowsPerPage?: number;
    onTotalCountChange?: (count: number) => void;
    paymentData?: PaymentHistoryItemNew[];
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
    currentPage = 0,
    rowsPerPage = 10,
    onTotalCountChange,
    paymentData,
}) => {
    const { t } = useTranslation();
    const showVerificationBanner = useContext(VerificationBannerContext);
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Transform data to match CommonTable structure
    const transformedRows = useMemo(() => {
        if (!paymentData || paymentData.length === 0) return [];

        // Sort payments by date (most recent first)
        const sortedPayments = sortByDateDesc(paymentData, payment => payment.paymentDate);

        return sortedPayments.map((payment: PaymentHistoryItemNew) =>
            mapPaymentHistoryRow(payment, formatToUSDate, formatCurrency),
        );
    }, [paymentData]);

    // Report total count to parent
    useEffect(() => {
        if (onTotalCountChange) {
            onTotalCountChange(transformedRows.length);
        }
    }, [transformedRows.length, onTotalCountChange]);

    // Paginate the rows
    const paginatedRows = useMemo(() => {
        const startIndex = currentPage * rowsPerPage;
        const endIndex = rowsPerPage === -1 ? transformedRows.length : startIndex + rowsPerPage;
        return transformedRows.slice(startIndex, endIndex);
    }, [transformedRows, currentPage, rowsPerPage]);

    const equalMinWidth = 120;
    const columns = [
        {
            id: 'contractId',
            label: t('payments.upcomingPayments.contractId') || 'Contract ID',
            sortable: false,
            minWidth: equalMinWidth,
        },
        {
            id: 'paymentDate',
            label: t('contracts.paymentHistory.paymentEffectiveDate') || 'Payment Effective Date',
            sortable: true,
            minWidth: 150,
        },
        {
            id: 'referenceNumber',
            label: t('contracts.paymentHistory.referenceNumber') || 'Reference Number',
            sortable: false,
            minWidth: 150,
        },
        {
            id: 'paymentType',
            label: t('payments.paymentHistory.method'),
            sortable: false,
            align: 'center' as const,
            minWidth: equalMinWidth,
        },
        {
            id: 'amount',
            label: t('payments.paymentHistory.amount'),
            sortable: false,
            align: 'right' as const,
            minWidth: equalMinWidth,
        },
    ];

    // Render accordion content with invoice details
    const renderAccordionContent = (row: Record<string, unknown>) => (
        <PaymentInvoiceAccordionContent invoices={row.invoices as PaymentInvoiceDetail[]} />
    );

    if (transformedRows.length === 0 && !isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={'calc(100dvh - 300px)'}>
                <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                    {t('common.NoRecordsAvailable')}
                </Typography>
            </Box>
        );
    }

    if (isMobileOrTablet) {
        return (
            <Stack gap={1.5} px={{ xs: 1.5, sm: 3 }}>
                {paginatedRows.map((row, rowIndex) => (
                    <PaymentHistoryCard key={rowIndex} row={row} />
                ))}
            </Stack>
        );
    }

    return (
        <Stack gap={3}>
            <Stack px={3}>
                <CommonTable
                    columns={columns}
                    rows={paginatedRows}
                    enableAccordion={true}
                    renderAccordionContent={renderAccordionContent}
                    stickyHeader
                    maxHeight={showVerificationBanner ? 'calc(100dvh - 384px)' : 'calc(100dvh - 346px)'}
                    className="accordionTableWrapper"
                    sx={getEqualWidthTableSx(columns.length)}
                />
            </Stack>
        </Stack>
    );
};

export default PaymentHistory;
